import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MessageCircle, X, Maximize2, Minimize2, ChevronDown, ChevronUp } from 'lucide-react';
import logo from '../../../../../assets/images/icons/logo.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { submitGenioStreaming, abortGenioStreaming } from '../actions';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { ChatRequest } from '../../../../../interfaces/api/chat';
import { ChatType } from '../../../../../shared/utils/enums';
import { TabWithCategories } from '../../../../../interfaces/api/chat/config';

interface GenioChatProps {
  isOpen: boolean;
  onClose: () => void;
  patientName: string;
  patientId: number;
}

interface Message {
  id: number;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  isMarkdown?: boolean;
}

export const GenioChat: React.FC<GenioChatProps> = ({
  isOpen,
  onClose,
  patientName,
  patientId,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const rawCategories = useSelector((state: RootState) => state.auth.categories);
  
  // Process categories to merge single-category tabs into clinical case tab
  const categories = useMemo(() => {
    const allTabs = [...rawCategories];
    const clinicalCaseTab = allTabs.find(tab => tab.tab_value === "clinical_cases");
    
    if (clinicalCaseTab) {
      // Separate single-category tabs and multi-category tabs
      const { singleCategoryTabs, multiCategoryTabs } = allTabs.reduce(
        (acc, tab: TabWithCategories) => {
          if (tab.tab_value === "clinical_cases") {
            acc.multiCategoryTabs.push(tab);
          } else if (tab.categories.length === 1) {
            acc.singleCategoryTabs.push(tab);
          } else {
            acc.multiCategoryTabs.push(tab);
          }
          return acc;
        },
        { singleCategoryTabs: [] as TabWithCategories[], multiCategoryTabs: [] as TabWithCategories[] }
      );

      // Add categories from single-category tabs to clinical case tab
      const singleCategories = singleCategoryTabs.flatMap((tab: TabWithCategories) => tab.categories);
      
      // Create a new clinical case tab with merged categories
      const updatedClinicalCaseTab = {
        ...clinicalCaseTab,
        categories: [...clinicalCaseTab.categories, ...singleCategories]
      };

      // Replace the clinical case tab in multiCategoryTabs
      const updatedMultiCategoryTabs = multiCategoryTabs.map((tab: TabWithCategories) => 
        tab.tab_value === "clinical_cases" ? updatedClinicalCaseTab : tab
      );

      return updatedMultiCategoryTabs;
    }

    return allTabs;
  }, [rawCategories]);

  const doctorName = (currentUser?.name || 'Doctor').toUpperCase();
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      type: 'ai',
      content: `Hi ${doctorName}, I'm here to assist with ${patientName}'s care. I can help summarise her current status, suggest next steps, or answer any clinical questions you have.`,
      timestamp: new Date()
    }
  ]);
  
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<number | null>(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [expandedTabId, setExpandedTabId] = useState<string | null>(null);
  
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamContentRef = useRef('');
  const currentMessageIdRef = useRef<number | null>(null);
  const messageIdCounterRef = useRef(2); // Start from 2 since we have initial message with id 1

  // Track whether to auto-scroll when new content arrives
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Cleanup streaming on component unmount
  useEffect(() => {
    return () => {
      abortGenioStreaming();
    };
  }, []);

  // Listen for user scrolls to pause/resume auto-scrolling
  useEffect(() => {
    const container = chatBodyRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // If within 50px of bottom, resume auto-scroll; otherwise pause
      const atBottom = scrollHeight - (scrollTop + clientHeight) < 50;
      setShouldAutoScroll(atBottom);
    };
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle opening and closing animations
  useEffect(() => {
    let animationTimer: NodeJS.Timeout;
    
    if (isOpen) {
      // Start opening animation - make visible first, then animate in
      setIsVisible(true);
      setIsAnimating(true);
      // Small delay to ensure DOM is ready for animation
      animationTimer = setTimeout(() => {
        setIsAnimating(false);
      }, 350); // Slightly longer to match the transition duration
    } else {
      // Start closing animation - animate out first, then hide
      setIsAnimating(true);
      animationTimer = setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
      }, 350); // Match transition duration
    }

    return () => {
      if (animationTimer) {
        clearTimeout(animationTimer);
      }
    };
  }, [isOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isAnimating) {
      const timer = setTimeout(() => {
        const input = document.querySelector('.chat-input') as HTMLTextAreaElement;
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isAnimating]);

  // Handle escape key for fullscreen and closing
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else if (isOpen) {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFullscreen, isOpen, onClose]);

  // Auto-scroll to bottom when new messages arrive, unless user has scrolled up
  useEffect(() => {
    if (shouldAutoScroll && chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, streamingContent, shouldAutoScroll]);

  const quickActions = [
    { id: 1, title: 'Summarise visit' },
    { id: 2, title: 'Suggest orders' },
    { id: 3, title: 'List discharge meds' },
    { id: 4, title: 'Interpret latest labs' }
  ];

  // Fixed addMessage function to ensure unique IDs
  const addMessage = (content: string, type: 'user' | 'ai' = 'user', id?: number) => {
    const messageId = id || messageIdCounterRef.current++;
    const newMessage: Message = {
      id: messageId,
      type,
      content,
      timestamp: new Date()
    };
    setMessages(prev => {
      const updated = [...prev, newMessage];
      return updated;
    });
    return messageId;
  };

  const updateMessage = (id: number, content: string) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === id 
          ? { ...msg, content }
          : msg
      )
    );
  };

  const streamAIResponse = async (query: string, isCategoryClick = false, categoryId?: number) => {
    // Abort any existing stream
    if (isStreaming) {
      abortGenioStreaming();
    }

    // Reset all streaming state
    setIsStreaming(true);
    setCurrentStreamingMessageId(null);
    setStreamingContent('');
    streamContentRef.current = '';
    currentMessageIdRef.current = null;
    
    let messageId: number | null = null;

    // Create the AI message with empty content - ensure unique ID
    messageId = addMessage('', 'ai');
    setCurrentStreamingMessageId(messageId);
    currentMessageIdRef.current = messageId;

    try {
      const request: ChatRequest = {
        question: query,
        type: ChatType.ClinicalCase,
        patient_id: patientId,
        prepare_clinical_case: true,
        category_id: categoryId
      };

      await submitGenioStreaming(
        {
          ...request,
          patientId: patientId,
        },
        (chunk: string) => {
          // Clean the chunk
          const cleanChunk = chunk.replace(/\[DONE\]/g, '');
          if (!cleanChunk) {
            return;
          }

          // Update content using callback to ensure we get the latest state
          setStreamingContent(prevContent => {
            const newContent = prevContent + cleanChunk;
            // Also update the ref for consistency
            streamContentRef.current = newContent;
            return newContent;
          });

          // Also update the message in real-time for better UX
          if (currentMessageIdRef.current) {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === currentMessageIdRef.current && msg.type === 'ai'
                  ? { ...msg, content: streamContentRef.current + cleanChunk }
                  : msg
              )
            );
          }
        },
        () => {
          setIsStreaming(false);
          setCurrentStreamingMessageId(null);
          setStreamingContent('');
          streamContentRef.current = '';
          currentMessageIdRef.current = null;
        },
        (error: string) => {
          console.error('Streaming error:', error);
          setIsStreaming(false);
          setCurrentStreamingMessageId(null);
          setStreamingContent('');
          streamContentRef.current = '';
          currentMessageIdRef.current = null;
        }
      );
    } catch (error) {
      console.error('Error in streamAIResponse:', error);
      setIsStreaming(false);
      setCurrentStreamingMessageId(null);
      setStreamingContent('');
      streamContentRef.current = '';
      currentMessageIdRef.current = null;
    }
  };

  const handleCategoryClick = async (category: { id: number; title: string }) => {
    if (isStreaming) return;
    
    addMessage(category.title, 'user'); // Show the label in chat
    
    // Close all sections
    setShowQuickActions(false);
    setExpandedTabId(null);
    
    await streamAIResponse('', true, category.id);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isStreaming) return;

    const userMessage = inputValue;
    setInputValue('');
    
    // Add user message
    addMessage(userMessage, 'user');
    
    // Close all sections
    setShowQuickActions(false);
    setExpandedTabId(null);
    
    // Stream AI response for custom queries
    await streamAIResponse(userMessage, false);
  };

  const handleQuickAction = async (action: string) => {
    if (isStreaming) return;
    
    addMessage(action, 'user');
    
    // Close all sections
    setShowQuickActions(false);
    setExpandedTabId(null);

    // Stream AI response
    await streamAIResponse(action, false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleTab = (tabId: string) => {
    setExpandedTabId(prevId => {
      const newId = prevId === tabId ? null : tabId;
      // Close quick actions when opening a category tab
      if (newId !== null) {
        setShowQuickActions(false);
      }
      return newId;
    });
  };

  const toggleQuickActions = () => {
    setShowQuickActions(prev => !prev);
    setExpandedTabId(null); // Close any open category tab
  };

  // Don't render anything if not visible and not animating
  if (!isVisible) return null;

  return (
    <>
      {/* Mobile overlay backdrop */}
      <div 
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
          isOpen && !isAnimating ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`} 
        onClick={onClose}
      />
      
      {/* Chat drawer */}
      <div 
        className={`fixed right-0 top-0 bottom-0 bg-white dark:bg-darkCardBackground z-50 flex flex-col shadow-2xl transition-all duration-300 ease-in-out ${
          isFullscreen 
            ? 'w-full h-full' 
            : 'w-full md:w-[480px]'
        } ${
          isOpen && !isAnimating
            ? 'translate-x-0' 
            : 'translate-x-full'
        }`}
        style={{
          transform: isOpen && !isAnimating ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <img src={logo} alt="Genio AI" className="w-4 h-4" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Genio Assistant</h3>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button 
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Chat messages area */}
        <div 
          ref={chatBodyRef}
          className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50 dark:bg-gray-900 space-y-3"
          style={{
            scrollBehavior: shouldAutoScroll ? 'smooth' : 'auto',
            overscrollBehavior: 'contain'
          }}
        >
          <div className="min-h-full flex flex-col">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`flex gap-2 ${message.type === 'user' ? 'flex-row-reverse' : ''} transition-all duration-200 ease-in-out`}
              >
                {/* Avatar */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 transition-all duration-200 ${
                  message.type === 'ai' 
                    ? 'bg-primary dark:bg-primary text-white' 
                    : 'bg-gray-600 dark:bg-gray-700 text-white'
                }`}>
                  {message.type === 'ai' ? 'G' : doctorName.split(' ').map((n: string) => n[0]).join('')}
                </div>

                {/* Message content */}
                <div className={`flex flex-col max-w-[85%] ${
                  message.type === 'user' ? 'items-end' : 'items-start'
                }`}>
                  <div className={`rounded-lg px-3 py-1.5 ${
                    message.type === 'user'
                      ? 'bg-primary dark:bg-primary text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                  }`}>
                    <div className="text-xs">
                    <MarkdownPreview
                          source={currentStreamingMessageId === message.id && isStreaming
                          ? streamingContent || message.content
                          : message.content}
                          className='markdown-body'
                          style={{ 
                            backgroundColor: 'transparent',
                            fontSize: '12px',
                            color: 'inherit'
                          }}
                          data-color-mode="auto"
                        />
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Typing/Streaming indicator */}
          {(isTyping || isStreaming) && (
            <div className="flex gap-2 sticky bottom-[-0.75rem] pb-1 bg-gray-50 dark:bg-gray-900 pt-2">
              <div className="w-6 h-6 rounded-full bg-primary dark:bg-primary flex items-center justify-center text-[10px] font-semibold text-white">
                G
              </div>
              <div className="flex items-center space-x-1.5 bg-white dark:bg-gray-800 rounded-lg px-3 py-1.5">
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Quick actions and categories */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground">
          {/* Quick Actions */}
          <div className="mb-2">
            <button
              onClick={toggleQuickActions}
              className="flex items-center justify-between w-full text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1.5 py-1.5 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              <span>Quick Actions</span>
              {showQuickActions ? 
                <ChevronUp className="w-3 h-3" /> : 
                <ChevronDown className="w-3 h-3" />
              }
            </button>
            
            {/* Collapsible Quick Actions */}
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
              showQuickActions ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="flex flex-wrap gap-1.5 max-h-[180px] overflow-y-auto pr-1">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.title)}
                    disabled={isStreaming}
                    className="text-[10px] px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-primary dark:hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {action.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Categories from backend */}
          {categories.map((tab: TabWithCategories) => (
            <div key={tab.id} className="mb-2">
              <button
                onClick={() => toggleTab(tab.id.toString())}
                className="flex items-center justify-between w-full text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1.5 py-1.5 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                <span>{tab.tab_name}</span>
                {expandedTabId === tab.id.toString() ? 
                  <ChevronUp className="w-3 h-3" /> : 
                  <ChevronDown className="w-3 h-3" />
                }
              </button>
              
              {/* Collapsible Categories */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                expandedTabId === tab.id.toString() ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="flex flex-wrap gap-1.5 max-h-[180px] overflow-y-auto pr-1">
                  {tab.categories.map((category: { id: number; title: string }, idx: number) => (
                    <button
                      key={category.id + idx}
                      onClick={() => handleCategoryClick(category)}
                      disabled={isStreaming}
                      className="text-[10px] px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-primary dark:hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {category.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Input area */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground">
          <div className="flex items-end gap-2">
            <textarea
              className="chat-input flex-1 resize-none rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary dark:focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary dark:focus:ring-primary-300"
              placeholder="Type your message..."
              rows={1}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                // Auto-resize based on content
                const textarea = e.target;
                textarea.style.height = 'auto';
                const newHeight = Math.min(textarea.scrollHeight, 120); // 120px is roughly 5 rows
                textarea.style.height = `${newHeight}px`;
              }}
              onKeyPress={handleKeyPress}
              disabled={isStreaming}
              style={{ minHeight: '32px', maxHeight: '120px' }}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isStreaming}
              className="inline-flex items-center justify-center rounded-lg bg-primary dark:bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isStreaming ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <MessageCircle className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};